import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import KioskHeader from "../components/KioskHeader";
import PwaInstallPrompt from "../components/PwaInstallPrompt";
import { useAuth } from "../context/AuthContext";
import { clearPhotos, getQueueStats, pruneOldPhotos } from "../services/localStore";
import {
  isOnline,
  syncPendingPunches,
  subscribeSyncStatus,
} from "../services/syncQueue";
import { BRAND, cardSx, primaryButtonSx } from "../styles/authTheme";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState({ pendingCount: 0, photoCount: 0 });
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(isOnline());

  const refreshStats = async () => {
    const data = await getQueueStats();
    setStats(data);
  };

  useEffect(() => {
    refreshStats();
    pruneOldPhotos(30).catch(console.error);

    const unsub = subscribeSyncStatus(({ syncing: isSyncing, pending }) => {
      setSyncing(Boolean(isSyncing));
      if (typeof pending === "number") {
        setStats((prev) => ({ ...prev, pendingCount: pending }));
      }
    });

    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      unsub();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const handleSync = async () => {
    try {
      const result = await syncPendingPunches();
      await refreshStats();
      toast.success(
        result.synced > 0
          ? `Synced ${result.synced} punch(es)`
          : "Nothing to sync"
      );
    } catch {
      toast.error("Sync failed");
    }
  };

  const handleClearPhotos = async () => {
    try {
      await clearPhotos();
      await refreshStats();
      toast.success("Local photos cleared");
    } catch {
      toast.error("Failed to clear photos");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/login", { replace: true });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg }}>
      <KioskHeader
        title="Settings"
        showBack
        backTo="/punch"
        showSettings={false}
        online={online}
        pendingCount={stats.pendingCount}
      />

      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 520, mx: "auto" }}>
        <Box sx={cardSx}>
          <Typography
            variant="h6"
            sx={{ color: BRAND.navy, fontWeight: 700, mb: 2 }}
          >
            Kiosk settings
          </Typography>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${BRAND.border}`,
              mb: 2,
            }}
          >
            <List disablePadding>
              <ListItem divider>
                <ListItemText
                  primary="Connection"
                  secondary={online ? "Online" : "Offline"}
                  primaryTypographyProps={{ fontWeight: 600, color: BRAND.navy }}
                  secondaryTypographyProps={{ color: BRAND.muted }}
                />
              </ListItem>
              <ListItem divider>
                <ListItemText
                  primary="Pending punches"
                  secondary={`${stats.pendingCount} waiting to sync`}
                  primaryTypographyProps={{ fontWeight: 600, color: BRAND.navy }}
                  secondaryTypographyProps={{ color: BRAND.muted }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Local photos"
                  secondary={`${stats.photoCount} stored on this device (not uploaded)`}
                  primaryTypographyProps={{ fontWeight: 600, color: BRAND.navy }}
                  secondaryTypographyProps={{ color: BRAND.muted }}
                />
              </ListItem>
            </List>
          </Paper>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<CloudSyncOutlinedIcon />}
            onClick={handleSync}
            disabled={syncing || !online}
            sx={{
              mb: 1.5,
              py: 1.3,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              borderColor: BRAND.orange,
              color: BRAND.orange,
            }}
          >
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleClearPhotos}
            sx={{
              mb: 1.5,
              py: 1.3,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              borderColor: BRAND.border,
              color: BRAND.muted,
            }}
          >
            Clear Local Photos
          </Button>

          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={() => navigate('/register-face')}
            sx={{
              ...primaryButtonSx,
              bgcolor: BRAND.orange,
              mb: 1.5,
              "&:hover": {
                bgcolor: "#cc5200",
                boxShadow: "0 4px 14px rgba(230,90,0,0.25)",
              },
            }}
          >
            Register Employee Face
          </Button>

          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<LogoutOutlinedIcon />}
            onClick={handleLogout}
            sx={{
              ...primaryButtonSx,
              bgcolor: BRAND.navy,
              "&:hover": {
                bgcolor: "#152238",
                boxShadow: "0 4px 14px rgba(26,43,74,0.25)",
              },
            }}
          >
            Log Out Employer
          </Button>

          <PwaInstallPrompt variant="link" />

          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 3, textAlign: "center", color: BRAND.muted, lineHeight: 1.6 }}
          >
            Photos are kept locally for worker verification and are never sent to
            the server.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsPage;
